/* ═══════════════════════════════════════════════════════════════
   Time & Expense rebuild (Tranche 4) — Vacation tab.

   Clean-sheet rebuild of the Vacation portion of the old
   TimeTrackingPage.tsx god-file. Workflow unchanged: request form →
   My Requests (+ cancel) → pending-approvals queue (deny w/ inline
   reason) → approved list → upcoming holidays. Forecast sync on
   approve/deny/cancel/delete stays entirely inside VacationService —
   untouched here (D4/T4 contract: don't touch the sync logic).
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { SearchableSelect } from '../../components/SearchableSelect';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';

import { Input } from '../../components/ui/input';
import { StatusBadge } from '../../components/StatusBadge';
import EmptyState from '../../components/EmptyState';
import { TeamService } from '../../services/teamService';
import { VacationService } from '../../services/vacationService';
import type { AuthUser } from '../../services/authService';
import { hasPermission, hasAnyPermission } from '../../../shared/permissions';
import type {
  TeamMember,
  VacationCategory,
  FirmHoliday,
  VacationRequest,
} from '../../../shared/types';

function settled<T>(r: PromiseSettledResult<T>, fallback: T): T {
  return r.status === 'fulfilled' ? r.value : fallback;
}

function getBusinessDays(start: string, end: string): number {
  let count = 0;
  const d = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  while (d <= e) {
    const dow = d.getDay();
    if (dow > 0 && dow < 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

export interface VacationTabProps {
  toast: (msg: string, isError?: boolean) => void;
  authUser?: AuthUser;
}

export function VacationTab({ toast, authUser }: VacationTabProps) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [categories, setCategories] = useState<VacationCategory[]>([]);
  const [holidays, setHolidays] = useState<FirmHoliday[]>([]);
  const [requests, setRequests] = useState<VacationRequest[]>([]);

  // Request form state
  const [categoryId, setCategoryId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState('8');
  const [note, setNote] = useState('');

  // Deny reason (bulk-bar action, not a native prompt)
  const [denyOpen, setDenyOpen] = useState(false);
  const [denyReason, setDenyReason] = useState('');

  const myId = authUser?.teamMemberId;
  const canApprove = hasPermission(authUser?.permissions, 'vacation.approve');
  // vacation.view_team was a client-only wire gap — the server already
  // row-scopes vacation_requests for view_team holders, but this panel only
  // rendered for view_all.
  const canViewAll = hasAnyPermission(authUser?.permissions, [
    'vacation.view_all',
    'vacation.view_team',
  ]);

  useEffect(() => {
    loadAll();
  }, []);
  useEffect(() => {
    const handler = () => loadAll();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, []);

  async function loadAll() {
    try {
      const [memsR, catsR, holsR, reqsR] = await Promise.allSettled([
        TeamService.list(),
        VacationService.categories.list(),
        VacationService.holidays.list(),
        VacationService.listRequests(),
      ]);
      const mems = settled(memsR, [] as TeamMember[]);
      const cats = settled(catsR, [] as VacationCategory[]);
      const hols = settled(holsR, [] as FirmHoliday[]);
      const reqs = settled(reqsR, [] as VacationRequest[]);
      setMembers(mems.filter((m) => m.active));
      setCategories(cats);
      setHolidays(hols.sort((a, b) => a.date.localeCompare(b.date)));
      setRequests(reqs);
      setCategoryId((prev) => prev || (cats.length > 0 ? cats[0].id : ''));
    } catch (err: any) {
      console.error('VacationTab loadAll failed:', err);
    }
  }

  function memberName(id?: string): string {
    if (!id) return '';
    return members.find((m) => m.id === id)?.name || '';
  }

  function categoryName(id: string): string {
    return categories.find((c) => c.id === id)?.name || '—';
  }

  const totalHours =
    startDate && endDate ? getBusinessDays(startDate, endDate) * (parseFloat(hoursPerDay) || 8) : 0;

  const myRequests = requests
    .filter((r) => r.teamMemberId === myId)
    .sort((a, b) => b.startDate.localeCompare(a.startDate));

  const pendingApprovals = canApprove
    ? requests
        .filter((r) => r.status === 'pending' && r.teamMemberId !== myId)
        .sort((a, b) => a.startDate.localeCompare(b.startDate))
    : [];

  const allApproved = canViewAll
    ? requests
        .filter((r) => r.status === 'approved')
        .sort((a, b) => a.startDate.localeCompare(b.startDate))
    : [];

  const upcomingHolidays = holidays.filter((h) => h.date >= new Date().toISOString().split('T')[0]);

  async function submitRequest() {
    if (!categoryId || !startDate || !endDate) {
      toast('Please fill in category, start date, and end date', true);
      return;
    }
    if (startDate > endDate) {
      toast('End date must be after start date', true);
      return;
    }
    if (!myId) {
      toast('No team member linked to your account', true);
      return;
    }
    const bDays = getBusinessDays(startDate, endDate);
    if (bDays === 0) {
      toast('No business days in selected range', true);
      return;
    }
    try {
      await VacationService.saveRequest({
        id: '',
        teamMemberId: myId,
        vacationCategoryId: categoryId,
        startDate,
        endDate,
        hoursPerDay: parseFloat(hoursPerDay) || 8,
        totalHours: bDays * (parseFloat(hoursPerDay) || 8),
        note: note.trim() || undefined,
        status: 'pending',
        createdAt: '',
        updatedAt: '',
      });
      setStartDate('');
      setEndDate('');
      setNote('');
      setHoursPerDay('8');
      await loadAll();
      toast('Vacation request submitted');
    } catch (err: any) {
      toast('Submit failed: ' + err.message, true);
    }
  }

  async function approveMany(items: VacationRequest[]) {
    if (!myId) return;
    const eligible = items.filter((r) => r.status === 'pending');
    if (!eligible.length) return;
    try {
      for (const r of eligible) await VacationService.approveRequest(r.id, myId);
      await loadAll();
      toast(`Approved ${eligible.length} request${eligible.length === 1 ? '' : 's'}`);
    } catch (err: any) {
      toast('Approve failed: ' + err.message, true);
    }
  }

  async function denyMany(items: VacationRequest[]) {
    if (!myId) return;
    const eligible = items.filter((r) => r.status === 'pending');
    if (!eligible.length) return;
    try {
      for (const r of eligible) {
        await VacationService.denyRequest(r.id, myId, denyReason.trim() || undefined);
      }
      setDenyOpen(false);
      setDenyReason('');
      await loadAll();
      toast(`Denied ${eligible.length} request${eligible.length === 1 ? '' : 's'}`);
    } catch (err: any) {
      toast('Deny failed: ' + err.message, true);
    }
  }

  const sectionTitle: React.CSSProperties = {
    fontSize: 'var(--font-size-base)',
    fontWeight: 700,
    color: 'var(--tx)',
    margin: '0 0 var(--space-3)',
  };
  const card: React.CSSProperties = {
    background: 'var(--sf)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--bd)',
    overflow: 'hidden',
  };
  const fieldLabel: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--tm)',
    marginBottom: rem(3),
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-5)',
        padding: 'var(--space-6)',
      }}
    >
      {/* Submit Request */}
      <div style={{ ...card, padding: 'var(--space-5)' }}>
        <h3 style={sectionTitle}>Submit Time-Off Request</h3>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <div>
            <div style={fieldLabel}>Category</div>
            <div style={{ minWidth: rem(180) }}>
              <SearchableSelect
                value={categoryId}
                options={categories.map((c) => ({ value: c.id, label: c.name }))}
                onChange={(v) => setCategoryId(v)}
                placeholder={categories.length === 0 ? 'No categories' : '-- Pick category --'}
                searchPlaceholder="Search categories…"
                clearable={false}
              />
            </div>
          </div>
          <div>
            <div style={fieldLabel}>Start Date</div>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <div style={fieldLabel}>End Date</div>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div>
            <div style={fieldLabel}>Hrs/Day</div>
            <Input
              type="number"
              value={hoursPerDay}
              onChange={(e) => setHoursPerDay(e.target.value)}
              step="0.5"
              min="0.5"
              max="24"
              style={{ width: rem(70), fontFamily: "'JetBrains Mono', monospace" }}
            />
          </div>
          <div style={{ flex: 1, minWidth: rem(160) }}>
            <div style={fieldLabel}>Note (optional)</div>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason…"
              style={{ width: '100%' }}
            />
          </div>
          <Button onClick={submitRequest} variant="default" style={{ whiteSpace: 'nowrap' }}>
            Submit Request
          </Button>
        </div>
        {totalHours > 0 && (
          <div
            style={{
              marginTop: 'var(--space-2)',
              fontSize: 'var(--font-size-base)',
              color: 'var(--tm)',
            }}
          >
            {getBusinessDays(startDate, endDate)} business day
            {getBusinessDays(startDate, endDate) !== 1 ? 's' : ''} &middot; {totalHours} total hours
          </div>
        )}
      </div>

      {/* My Requests */}
      <div style={card}>
        <div style={{ padding: 'var(--space-4) var(--space-4) 0' }}>
          <h3 style={sectionTitle}>My Requests</h3>
        </div>
        <DataTable<VacationRequest>
          columns={[
            {
              key: 'category',
              label: 'Category',
              render: (r) => categoryName(r.vacationCategoryId),
              getValue: (r) => categoryName(r.vacationCategoryId),
            },
            { key: 'startDate', label: 'Start', getValue: (r) => r.startDate },
            { key: 'endDate', label: 'End', getValue: (r) => r.endDate },
            {
              key: 'hoursPerDay',
              label: 'Hrs/Day',
              width: 80,
              render: (r) => (
                <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.hoursPerDay}</span>
              ),
              getValue: (r) => r.hoursPerDay,
            },
            {
              key: 'totalHours',
              label: 'Total',
              width: 80,
              render: (r) => (
                <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {r.totalHours || '—'}
                </span>
              ),
              getValue: (r) => r.totalHours || 0,
            },
            {
              key: 'status',
              label: 'Status',
              width: 100,
              render: (r) => <StatusBadge status={r.status} />,
              getValue: (r) => r.status,
            },
            {
              key: 'note',
              label: 'Note',
              render: (r) => (
                <span style={{ color: 'var(--tm)', fontSize: 'var(--font-size-sm)' }}>
                  {r.note || '—'}
                  {r.denialReason ? ` (Denied: ${r.denialReason})` : ''}
                </span>
              ),
              getValue: (r) => r.note || '',
            },
          ]}
          data={myRequests}
          keyField="id"
          storageKey="vacation-my-requests"
          emptyTitle="No Requests"
          emptyMessage="No vacation requests yet."
          permissions={authUser?.permissions}
          bulkActions={{
            delete: {
              permission: 'vacation.delete',
              label: 'Cancel',
              onDelete: async (items) => {
                const eligible = items.filter((r) => r.status === 'pending');
                for (const r of eligible) await VacationService.deleteRequest(r.id);
                await loadAll();
                toast(
                  eligible.length === items.length
                    ? 'Requests cancelled'
                    : `Cancelled ${eligible.length} of ${items.length} (only pending requests can be cancelled)`
                );
              },
              confirmTitle: 'Cancel these vacation requests?',
              confirmDescription: (n) =>
                `${n} pending request${n === 1 ? '' : 's'} will be cancelled.`,
            },
            exportCsv: { permission: 'vacation.export', filename: 'my-vacation-requests' },
          }}
        />
      </div>

      {/* Pending Approvals (permission-gated) */}
      {canApprove && (
        <div style={card}>
          <div style={{ padding: 'var(--space-4) var(--space-4) 0' }}>
            <h3 style={sectionTitle}>Pending Approvals</h3>
          </div>
          <DataTable<VacationRequest>
            columns={[
              {
                key: 'member',
                label: 'Member',
                render: (r) => (
                  <span style={{ fontWeight: 600 }}>{memberName(r.teamMemberId)}</span>
                ),
                getValue: (r) => memberName(r.teamMemberId),
              },
              {
                key: 'category',
                label: 'Category',
                render: (r) => categoryName(r.vacationCategoryId),
                getValue: (r) => categoryName(r.vacationCategoryId),
              },
              { key: 'startDate', label: 'Start', getValue: (r) => r.startDate },
              { key: 'endDate', label: 'End', getValue: (r) => r.endDate },
              {
                key: 'hoursPerDay',
                label: 'Hrs/Day',
                width: 80,
                render: (r) => (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.hoursPerDay}</span>
                ),
                getValue: (r) => r.hoursPerDay,
              },
              {
                key: 'totalHours',
                label: 'Total',
                width: 80,
                render: (r) => (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {r.totalHours || '—'}
                  </span>
                ),
                getValue: (r) => r.totalHours || 0,
              },
              {
                key: 'note',
                label: 'Note',
                render: (r) => (
                  <span style={{ color: 'var(--tm)', fontSize: 'var(--font-size-sm)' }}>
                    {r.note || '—'}
                  </span>
                ),
                getValue: (r) => r.note || '',
              },
            ]}
            data={pendingApprovals}
            keyField="id"
            storageKey="vacation-pending-approvals"
            emptyTitle="No Pending Requests"
            emptyMessage="No pending requests to review."
            permissions={authUser?.permissions}
            bulkActions={{
              exportCsv: { permission: 'vacation.export', filename: 'vacation-pending-approvals' },
              extra: (items, meta) =>
                denyOpen ? (
                  <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center' }}>
                    <Input
                      value={denyReason}
                      onChange={(e) => setDenyReason(e.target.value)}
                      placeholder="Reason…"
                      style={{ width: rem(140), height: rem(28), fontSize: 'var(--font-size-xs)' }}
                      autoFocus
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={async () => {
                        await denyMany(items);
                        meta?.clear();
                      }}
                    >
                      Deny {items.length}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Cancel"
                      aria-label="Cancel deny"
                      onClick={() => {
                        setDenyOpen(false);
                        setDenyReason('');
                      }}
                    >
                      <Icon icon={X} em={1.0769} />
                    </Button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={async () => {
                        await approveMany(items);
                        meta?.clear();
                      }}
                    >
                      Approve {items.length}
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => setDenyOpen(true)}>
                      Deny
                    </Button>
                  </div>
                ),
            }}
          />
        </div>
      )}

      {/* Approved Time Off (visible to managers) */}
      {canViewAll && allApproved.length > 0 && (
        <div style={card}>
          <div style={{ padding: 'var(--space-4) var(--space-4) 0' }}>
            <h3 style={sectionTitle}>Approved Time Off</h3>
          </div>
          <DataTable<VacationRequest>
            columns={[
              {
                key: 'member',
                label: 'Member',
                render: (r) => (
                  <span style={{ fontWeight: 600 }}>{memberName(r.teamMemberId)}</span>
                ),
                getValue: (r) => memberName(r.teamMemberId),
              },
              {
                key: 'category',
                label: 'Category',
                render: (r) => categoryName(r.vacationCategoryId),
                getValue: (r) => categoryName(r.vacationCategoryId),
              },
              { key: 'startDate', label: 'Start', getValue: (r) => r.startDate },
              { key: 'endDate', label: 'End', getValue: (r) => r.endDate },
              {
                key: 'totalHours',
                label: 'Total Hours',
                width: 110,
                render: (r) => (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {r.totalHours || '—'}
                  </span>
                ),
                getValue: (r) => r.totalHours || 0,
              },
            ]}
            data={allApproved}
            keyField="id"
            storageKey="vacation-approved"
            emptyTitle="No Approved Time Off"
            emptyMessage="No approved time off to display."
            permissions={authUser?.permissions}
            bulkActions={{
              exportCsv: { permission: 'vacation.export', filename: 'vacation-approved' },
            }}
          />
        </div>
      )}

      {/* Upcoming Holidays */}
      <div style={card}>
        <div style={{ padding: 'var(--space-4) var(--space-4) 0' }}>
          <h3 style={sectionTitle}>Upcoming Firm Holidays</h3>
        </div>
        {upcomingHolidays.length === 0 ? (
          <EmptyState
            title="No upcoming holidays"
            subtitle="Holidays can be set in Admin > Time Off > Firm Holidays."
          />
        ) : (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--space-2)',
              padding: 'var(--space-3) var(--space-4) var(--space-4)',
            }}
          >
            {upcomingHolidays.map((h) => (
              <div
                key={h.id}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  background: 'rgba(var(--acr),0.06)',
                  border: '1px solid rgba(var(--acr),0.15)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: rem(2),
                }}
              >
                <span
                  style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--tx)' }}
                >
                  {h.name}
                </span>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                  {h.date} &middot; {h.hours}h
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default VacationTab;
